export type AccountingTypeGroup =
  | 'raw-material'
  | 'primary-packaging'
  | 'secondary-packaging'
  | 'transport-packaging'
  | 'packaging'
  | 'intermediate'
  | 'semi-finished'
  | 'finished-product'
  | 'cost';

export interface AccountingTypeGroupSource {
  group?: string | null;
  accountingType?: AccountingTypeGroupSource | null;
}

const groups = new Set<AccountingTypeGroup>([
  'raw-material',
  'primary-packaging',
  'secondary-packaging',
  'transport-packaging',
  'packaging',
  'intermediate',
  'semi-finished',
  'finished-product',
  'cost',
]);

export const groupOf = (source?: AccountingTypeGroupSource | null): AccountingTypeGroup | undefined => {
  const accountingType = source?.accountingType ?? source;
  return accountingType?.group && groups.has(accountingType.group as AccountingTypeGroup)
    ? accountingType.group as AccountingTypeGroup
    : undefined;
};

export const isFinishedProduct = (source?: AccountingTypeGroupSource | null): boolean =>
  groupOf(source) === 'finished-product';

export const isIntermediateOrSemi = (source?: AccountingTypeGroupSource | null): boolean => {
  const group = groupOf(source);
  return group === 'intermediate' || group === 'semi-finished';
};

export const isPrimaryPackaging = (source?: AccountingTypeGroupSource | null): boolean =>
  groupOf(source) === 'primary-packaging';

export const isSecondaryPackaging = (source?: AccountingTypeGroupSource | null): boolean =>
  groupOf(source) === 'secondary-packaging';

export const isTransportPackaging = (source?: AccountingTypeGroupSource | null): boolean =>
  groupOf(source) === 'transport-packaging';

// All packaging groups, including the legacy unspecified 'packaging'.
export const isPackaging = (source?: AccountingTypeGroupSource | null): boolean => {
  const group = groupOf(source);
  return group === 'primary-packaging'
    || group === 'secondary-packaging'
    || group === 'transport-packaging'
    || group === 'packaging';
};

export const isMaterial = (source?: AccountingTypeGroupSource | null): boolean =>
  groupOf(source) === 'raw-material' || isPackaging(source);

export const isCost = (source?: AccountingTypeGroupSource | null): boolean =>
  groupOf(source) === 'cost';
